/**
 * Öğe referansı — **geç bağlanan** öğeler için.
 *
 * ## Sorun (üç kez oldu)
 *
 * `useEffect(..., [videoRef])` yazmak, öğenin **ilk render'da DOM'da
 * olduğunu** varsayar. Oysa bizim ekranlarımızda öğe sonradan gelir:
 *
 *   - `WatchScreen`, oynatma hazır değilken `<Preparing>` döndürür → `<video>`
 *     DOM'da **yok**,
 *   - kanca o an `ref.current === null` görür, erken döner,
 *   - `[videoRef]` **hiç değişmez** (ref nesnesi sabittir) → etiket bir daha
 *     çalışmaz.
 *
 * Gerçek sunucuda bu üç ayrı hataya yol açtı:
 *
 *   1. altyazı ölçeği hiç ölçülmedi (`45vh` yedeğine düştü, altyazı kocaman),
 *   2. kontrol çubuğu hiç bağlanmadı (süre 0:00'da dondu, oynat ikonu
 *      hiç değişmedi),
 *   3. tam ekran/altyazı hedefi yanlış kalıntı.
 *
 * Her biri "yeşil test", "hata yok" görüntüsü verdi: kod doğruydu, sadece
 * **çağrılmadı**.
 *
 * ## Çözüm
 *
 * Öğe üç biçimde sunulur:
 *
 *   - `ref`   → nesne referansı (okuyan kancalar: `stage.ref.current`)
 *   - `attach` → **geri çağırma referansı**; `<div ref={attach}>`
 *   - `element` → durum; `useEffect` bunu bağımlılık olarak alabilir
 *
 * Böylece etiket "öğe DOM'a girdi" diye yeniden kurulur.
 *
 * ## Neden ayrı dosya?
 *
 * Desen iki yerede birden kullanılıyor ve **her seferinde yanlış yapılma
 * eğilimi** var. Tek yerde toplanınca doğru kullanım tek sezgi olur.
 */

import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';

export interface ElementRef<T extends HTMLElement> {
  /** `<div ref={attach}>` olarak kullanılacak geri çağırma referansı. */
  attach: (element: T | null) => void;
  /** Nesne referansı — yalnız **okumak** için (`ref.current`). */
  ref: RefObject<T | null>;
  /** Öğe DOM'a girdiğinde güncellenir; `useEffect` bağımlılığı olarak kullanılır. */
  element: T | null;
}

export function useElementRef<T extends HTMLElement>(): ElementRef<T> {
  const objectRef = useRef<T | null>(null);
  const [element, setElement] = useState<T | null>(null);

  /*
   * `attach` tek yer yazar: önce nesne referansı, sonra durum.
   *
   * Burada `useEffect(() => { objectRef.current = element })` **yanlış** olurdu:
   * öğe commit'te `attach` ile atanır, ama etiket render'daki (henüz `null`)
   * değerle çalışır ve `ref.current`'u ezerdi. Regresyon testi bunu yakaladı.
   */
  const attach = useCallback((node: T | null) => {
    objectRef.current = node;
    setElement(node);
  }, []);

  return { attach, ref: objectRef, element };
}
